import BigNumber from 'bignumber.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import duration from 'dayjs/plugin/duration';
import { isNumber, isNaN } from 'lodash-es';
import { formatUnits } from 'viem';
import { openTelegramLink, openLink as openLinkSdk, init } from '@telegram-apps/sdk';

BigNumber.config({ DECIMAL_PLACES: 50, EXPONENTIAL_AT: 1e9 });

export function isValidPositiveNumber(input: string): boolean {
  return isNumber(+input) && !isNaN(+input) && +input > 0;
}

dayjs.extend(utc);
dayjs.extend(duration);

export const displayTimeByUnix = (unix: number, format?: string) => {
  return `${dayjs
    .unix(unix)
    .utc()
    .format(format || 'YYYY-MM-DD HH:mm:ss')} UTC`;
};

export const formatNumber = (amount: string) => {
  const bn = new BigNumber(amount);

  if (!bn.isFinite() || bn.isNaN()) {
    return amount;
  }
  return bn.toFormat({
    groupSeparator: ',',
    groupSize: 3,
    decimalSeparator: '.'
  });
};

export const isBeforeByUnix = (unix: number) => {
  return dayjs(new Date()).isBefore(dayjs.unix(unix), 'millisecond');
};

export const isAfterByUnix = (unix: number) => {
  return dayjs(new Date()).isAfter(dayjs.unix(unix), 'millisecond');
};

export const safePlus = (a: string, b: string) => {
  return new BigNumber(a).plus(b).toString();
};

export const safeMultiply = (a: string, b: string) => {
  return new BigNumber(a).multipliedBy(b).toString();
};

// Format token amount from bigint to display string
export const formatTokenAmount = (amount: bigint, decimals = 18) => {
  return formatUnits(amount, decimals);
};

export const formatAmount = (amount: string) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  }).format(parseFloat(amount));
};

// Format time duration in seconds to human readable format
export const formatTimeUntilUnlock = (seconds: number) => {
  if (seconds <= 0) return 'Now';

  const dur = dayjs.duration(seconds, 'seconds');
  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

export const isLocal = window.location.href.includes('localhost');

export const isTelegramApp = (): boolean => {
  return !!window.TelegramWebviewProxy;
};

export const openLink = (url: string, openInTg = true) => {
  if (isTelegramApp()) {
    init();
    if (openInTg) {
      openTelegramLink(url);
    } else {
      openLinkSdk(url, { tryBrowser: 'chrome' });
    }
  } else {
    window.open(url);
  }
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
